import auth, { getLoggedInUser, getTenant } from "cp-client-auth!sofe";
import { getAllLocalStorageFeatureToggles } from "feature-toggles!sofe";
import { captureMessage } from "error-logging!sofe";
import { cloneDeep, isEqual, isPlainObject, without } from "lodash";

export class CanopyError extends Error {
  status;
}

const TRACKER_MAX_CALLS = 29;
const TRACKER_MAX_TIME = 30000;
let trackedCalls = [];

export const getCalls = () => trackedCalls;

/**
 * Detect "runaway" duplicate calls — same args fired more than 29 times in
 * 30 seconds. Used by both fetcherBase and fetchAsStream so duplicate
 * detection spans both code paths and a runaway loop can't slip through by
 * mixing the two.
 *
 * @param {unknown[]} args
 * @returns {{ isInfiniteLoop: boolean, count: number }}
 */
export function trackCall(args) {
  const duplicates = trackedCalls.filter(
    (c) => isEqual(c.args, args) && Date.now() - c.time < TRACKER_MAX_TIME
  );
  if (duplicates.length > TRACKER_MAX_CALLS) {
    return { isInfiniteLoop: true, count: duplicates.length };
  }
  const call = { args: cloneDeep(args), time: Date.now() };
  trackedCalls.push(call);
  setTimeout(() => {
    trackedCalls = without(trackedCalls, call);
  }, TRACKER_MAX_TIME);
  return { isInfiniteLoop: false, count: duplicates.length };
}

/**
 * In a pre-auth flow (magic-link, link-expired recovery, etc.) the session
 * is identified by a `session_id` query param rather than an auth cookie.
 *
 * @returns {string | null}
 */
export function getPreAuthSessionId() {
  return new URLSearchParams(window.location.search).get("session_id");
}

/**
 * If we're in a pre-auth flow, set the Limited-Access header carrying the
 * session id and return that id. Returns null when not in a pre-auth flow.
 * Mutates `headers`.
 *
 * @param {Headers} headers
 * @returns {string | null}
 */
export function applyPreAuthHeader(headers) {
  const sessionId = getPreAuthSessionId();
  if (sessionId) {
    headers.set("Limited-Access", sessionId);
  }
  return sessionId;
}

/**
 * Pre-auth flows must omit credentials so the cookie isn't sent alongside
 * the Limited-Access header. All other requests default to "include".
 *
 * @param {RequestCredentials | undefined} optionsCredentials
 * @param {boolean} isPreAuth
 * @returns {RequestCredentials}
 */
export function resolveCredentials(optionsCredentials, isPreAuth) {
  if (isPreAuth && optionsCredentials !== "omit") return "omit";
  return optionsCredentials ?? "include";
}

/**
 * Prepare a body for fetch: stringify plain objects, defaulting Content-Type
 * to application/json. Strings, FormData, Blob, etc. pass through unchanged.
 * Falsy bodies (null, undefined, "", 0, false) are returned unchanged. Mutates
 * `headers` when defaulting Content-Type.
 *
 * @param {unknown} body
 * @param {Headers} headers
 * @returns {BodyInit | undefined}
 */
export function normalizeBody(body, headers) {
  if (!body) return body;
  if (isPlainObject(body)) {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    try {
      return JSON.stringify(body);
    } catch (err) {
      // JSON.stringify can fail on circular references or BigInt values.
      // Surface to Sentry so the failure isn't silent — fetch will still
      // try to use the body and produce a confusing downstream error.
      captureMessage(err);
      return body;
    }
  }
  return body;
}

export const handleFetcherResponse = (
  fetcherPromise,
  responseType = "default",
  originalError
) =>
  fetcherPromise.then((response) => {
    if (response.ok) {
      if (response.status === 204) return null; // 204 No Content

      if (responseType === "default") {
        return response
          .text()
          .then((text) => {
            try {
              return JSON.parse(text);
            } catch {
              return text;
            }
          })
          .catch(handleUnknownError);
      } else {
        return response[responseType]().catch(handleUnknownError);
      }
    } else {
      originalError.message = `Request error: ${response.status} ${response.url} ${response.statusText}`;
      originalError.status = response.status;
      // Try to get the response body and include it in the error
      return response
        .text()
        .then((body) => {
          originalError.message += `. ${body}`;
          try {
            originalError.data = JSON.parse(body);
          } catch {
            originalError.data = body;
          }
          throw originalError;
        })
        .catch(() => {
          throw originalError;
        });
    }
  });

export function setHeaders(setHeader) {
  const csrfToken = auth.getCSRFToken();
  if (csrfToken) {
    setHeader("X-CSRF-TOKEN", csrfToken);
  }

  const user = getLoggedInUser();
  if (user) {
    setHeader("X-Canopy-User-ID", user.id);
  }

  const tenant = getTenant();
  if (tenant) {
    setHeader("X-Canopy-Tenant-ID", tenant.id);
  }

  const localStorageFeatureToggles = getAllLocalStorageFeatureToggles();
  if (Object.keys(localStorageFeatureToggles).length) {
    setHeader("X-Canopy-Overrides", JSON.stringify(localStorageFeatureToggles));
  }

  const backendOverride = window.localStorage.getItem("exchange-override");
  if (backendOverride) {
    setHeader("CTX-Exchange-Override", backendOverride);
  }

  setHeader(
    "client_app_name",
    window.appIsMobile ? "mobile-web-app" : "web-app"
  );
}

export function handleUnknownError(error) {
  if (error instanceof CanopyError) throw error;
  error.showToast = false;
  captureMessage(error);
  return Promise.reject(error);
}
